import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { maintenanceService } from '../../services/maintenanceService';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Alert from '../ui/Alert';
import LoadingSpinner from '../ui/LoadingSpinner';
import { Settings, Eye, EyeOff, Monitor, Users, CheckCircle } from 'lucide-react';

export default function MaintenanceToggle() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isMaintenanceMode: false,
    isCommunityTestingEnded: false,
    message: '',
    feedbackUrl: '',
    lastUpdated: null,
    updatedBy: null
  });

  const [formData, setFormData] = useState({
    message: '',
    feedbackUrl: ''
  });

  useEffect(() => {
    loadMaintenanceStatus();
  }, []);

  const loadMaintenanceStatus = async () => {
    try {
      setLoading(true);
      const status = await maintenanceService.getMaintenanceStatus();
      setMaintenanceStatus(status);
      setFormData({
        message: status.message,
        feedbackUrl: status.feedbackUrl
      });
    } catch (error) {
      console.error('Error loading maintenance status:', error);
      setError('Failed to load maintenance status');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await maintenanceService.setMaintenanceStatus(
        !maintenanceStatus.isMaintenanceMode,
        formData.message,
        formData.feedbackUrl,
        user.userId,
        maintenanceStatus.isCommunityTestingEnded
      );

      setSuccess(`Maintenance mode ${!maintenanceStatus.isMaintenanceMode ? 'enabled' : 'disabled'} successfully`);
      await loadMaintenanceStatus();
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      setError('Failed to update maintenance mode');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCommunityTesting = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await maintenanceService.setMaintenanceStatus(
        maintenanceStatus.isMaintenanceMode,
        formData.message,
        formData.feedbackUrl,
        user.userId,
        !maintenanceStatus.isCommunityTestingEnded
      );

      setSuccess(`Community testing ended mode ${!maintenanceStatus.isCommunityTestingEnded ? 'enabled' : 'disabled'} successfully`);
      await loadMaintenanceStatus();
    } catch (error) {
      console.error('Error toggling community testing mode:', error);
      setError('Failed to update community testing mode');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await maintenanceService.setMaintenanceStatus(
        maintenanceStatus.isMaintenanceMode,
        formData.message,
        formData.feedbackUrl,
        user.userId,
        maintenanceStatus.isCommunityTestingEnded
      );

      setSuccess('Maintenance settings updated successfully');
      await loadMaintenanceStatus();
    } catch (error) {
      console.error('Error updating maintenance settings:', error);
      setError('Failed to update maintenance settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
          <LoadingSpinner size="lg" />
          <span className="sr-only">Loading maintenance settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Community Testing Ended Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Community Testing Status
            </h3>
            <p className="text-gray-600 mt-1">
              Show community testing ended page to logged-in users
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              maintenanceStatus.isCommunityTestingEnded 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {maintenanceStatus.isCommunityTestingEnded ? 'TESTING ENDED' : 'TESTING ACTIVE'}
            </div>
            
            <Button
              variant={maintenanceStatus.isCommunityTestingEnded ? 'outline' : 'primary'}
              onClick={handleToggleCommunityTesting}
              disabled={saving}
              className="flex items-center gap-2"
            >
              {maintenanceStatus.isCommunityTestingEnded ? (
                <>
                  <Eye className="w-4 h-4" />
                  Resume Testing
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  End Testing
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> When community testing ended mode is active, logged-in users will see a 
            "thank you" page instead of the normal dashboard. This is separate from maintenance mode.
          </p>
        </div>
      </div>

      {/* Maintenance Mode Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6" aria-hidden="true" />
              Maintenance Mode
            </h3>
            <p className="text-gray-600 mt-1">
              Control site-wide maintenance mode for all users
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div 
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                maintenanceStatus.isMaintenanceMode 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}
              role="status"
              aria-live="polite"
              aria-label={`Maintenance mode is currently ${maintenanceStatus.isMaintenanceMode ? 'enabled' : 'disabled'}`}
            >
              {maintenanceStatus.isMaintenanceMode ? 'MAINTENANCE ON' : 'MAINTENANCE OFF'}
            </div>
            
            <Button
              variant={maintenanceStatus.isMaintenanceMode ? 'outline' : 'primary'}
              onClick={handleToggleMaintenance}
              disabled={saving}
              className="flex items-center gap-2"
              aria-label={`${maintenanceStatus.isMaintenanceMode ? 'Disable' : 'Enable'} maintenance mode for all users`}
            >
              {maintenanceStatus.isMaintenanceMode ? (
                <>
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                  Disable Maintenance
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" aria-hidden="true" />
                  Enable Maintenance
                </>
              )}
            </Button>
          </div>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}
        {success && <Alert variant="success" className="mb-4">{success}</Alert>}

        {/* Maintenance Settings */}
        <div className="space-y-4">
          <div>
            <label htmlFor="maintenance-message" className="block text-sm font-medium text-gray-700 mb-2">
              Maintenance Message
            </label>
            <textarea
              id="maintenance-message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Message to display to users during maintenance..."
              aria-describedby="maintenance-message-help"
            />
            <p id="maintenance-message-help" className="mt-1 text-sm text-gray-500">
              This message will be displayed to users when maintenance mode is active
            </p>
          </div>

          <div>
            <Input
              type="url"
              label="Feedback URL"
              value={formData.feedbackUrl}
              onChange={(e) => setFormData({ ...formData, feedbackUrl: e.target.value })}
              placeholder="https://docs.google.com/forms/..."
              helpText="Optional URL where users can provide feedback during maintenance"
            />
          </div>

          <Button
            variant="outline"
            onClick={handleUpdateSettings}
            disabled={saving}
            className="w-full"
          >
            Update Settings
          </Button>
        </div>

        {/* Status Info */}
        {maintenanceStatus.lastUpdated && (
          <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
            <p>
              Last updated: {new Date(maintenanceStatus.lastUpdated).toLocaleString()}
              {maintenanceStatus.updatedBy && ` by ${maintenanceStatus.updatedBy}`}
            </p>
          </div>
        )}
      </div>

      {/* Admin View Mode Controls */}
      <AdminViewModeControls />
    </div>
  );
}

function AdminViewModeControls() {
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('certifiable_admin_view_mode') || 'admin';
  });

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('certifiable_admin_view_mode', mode);
    // Refresh the page to apply the new view mode
    window.location.reload();
  };

  const viewModes = [
    {
      id: 'admin',
      label: 'Admin View',
      description: 'Full admin dashboard access',
      icon: Settings
    },
    {
      id: 'user',
      label: 'User View',
      description: 'See what regular users see (including maintenance mode)',
      icon: Eye
    },
    {
      id: 'live',
      label: 'Live Dashboard',
      description: 'See live dashboard even during maintenance',
      icon: Monitor
    }
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 id="view-mode-heading" className="text-xl font-bold text-gray-900 mb-4">Admin View Mode</h3>
      <p className="text-gray-600 mb-6">
        Control how you view the application as an admin
      </p>

      <div 
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
        role="radiogroup"
        aria-labelledby="view-mode-heading"
      >
        {viewModes.map((mode) => {
          const Icon = mode.icon;
          const isActive = viewMode === mode.id;
          
          return (
            <button
              key={mode.id}
              onClick={() => handleViewModeChange(mode.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                isActive
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
              role="radio"
              aria-checked={isActive}
              aria-describedby={`${mode.id}-description`}
              aria-label={`Switch to ${mode.label}: ${mode.description}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Icon className="w-5 h-5" aria-hidden="true" />
                <span className="font-medium">{mode.label}</span>
              </div>
              <p id={`${mode.id}-description`} className="text-sm opacity-75">{mode.description}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Current mode:</strong> {viewModes.find(m => m.id === viewMode)?.label}
        </p>
      </div>
    </div>
  );
}